<style scoped>
body {
	background-color: #f8f9fa;
	min-height: 100vh;
	display: flex;
	flex-direction: column;
}
body > .container-fluid {
	flex: 1;
}
.progress-bar {
	background-color: #0d6efd;
	transition: width 0.3s ease;
}
.reference-number {
	text-align: center;
	color: #6c757d;
	font-size: 0.9rem;
	margin-top: 24px;
}
</style>
<template>
	<router-view v-slot="{ Component }">
		<LoadingOverlay :is-loading="isLoading" loading-text="Loading report">
			<template v-if="!isLoading">
				<div class="container-fluid px-3 py-3">
					<div v-if="isProgressShown">
						<div v-if="district">
							<HeaderCompliance :district="district" />
						</div>
						<ProgressBarCompliance :step="stepCurrent" :total="stepTotal" />
					</div>

					<component
						:is="Component"
						:district="district"
						@doAddress="doAddress"
						@doContact="doContact"
						@doEvidence="doEvidence"
						@doPermission="doPermission"
						@doSubmit="doSubmit"
						:isUploading="isUploading"
						:publicID="report?.public_id ?? 'unset'"
						v-model="report"
					/>
				</div>
			</template>
		</LoadingOverlay>
	</router-view>
	<ErrorNotification
		v-if="errorMessage"
		:message="errorMessage"
		@dismissed="errorMessage = ''"
	/>
	<!-- Reference Number -->
	<div class="reference-number" v-if="report && report.public_id">
		<small>
			Reference number: <strong>{{ report.public_id }}</strong>
		</small>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { computedAsync } from "@vueuse/core";
import { useRouter } from "vue-router";

import type { Image } from "@/components/ImageUpload.vue";
import { log } from "@/log";
import errors from "@/rmo/errors";
import { ROUTE_NAMES } from "@/rmo/route/name";
import { useRoutes } from "@/rmo/route/use";
import { useStoreDistrict } from "@/rmo/store/district";
import { useStoreLocal } from "@/store/local";
import { useStoreLocation } from "@/store/location";
import { useStorePublicReport } from "@/rmo/store/publicreport";
import ErrorNotification from "@/rmo/components/ErrorNotification.vue";
import HeaderCompliance from "@/rmo/components/HeaderCompliance.vue";
import LoadingOverlay from "@/components/LoadingOverlay.vue";
import ProgressBarCompliance from "@/rmo/components/ProgressBarCompliance.vue";
import Intro from "@/rmo/content/compliance/Intro.vue";
import {
	type ComplianceUpdate,
	type District,
	PublicReport,
	PublicReportCompliance,
	PublicReportComplianceOptions,
} from "@/type/api";
import { Contact, Address, Location, PermissionType } from "@/type/api";

interface Props {
	public_id: string;
}

const district = ref<District | undefined>(undefined);
const errorMessage = ref<string>("");
const isLoading = ref<boolean>(true);
const isProgressShown = ref<boolean>(true);
const isUploading = ref<boolean>(false);
const props = defineProps<Props>();
const report = ref<PublicReportCompliance | undefined>(undefined);
const router = useRouter();
const routes = useRoutes();
const storeDistrict = useStoreDistrict();
const storeLocal = useStoreLocal();
const storeLocation = useStoreLocation();
const storePublicReport = useStorePublicReport();
const stepCurrent = computed((): number => {
	const n = router.currentRoute.value.name;
	let has_concerns = false;
	if (report.value && report.value.concerns.length > 0) {
		has_concerns = true;
	}
	if (n == ROUTE_NAMES.COMPLIANCE_INTRO) {
		return 1;
	} else if (n == ROUTE_NAMES.COMPLIANCE_ADDRESS) {
		return 2;
	} else if (n == ROUTE_NAMES.COMPLIANCE_CONCERN) {
		return 3;
	} else if (n == ROUTE_NAMES.COMPLIANCE_EVIDENCE) {
		return 3 + (has_concerns ? 1 : 0);
	} else if (n == ROUTE_NAMES.COMPLIANCE_PERMISSION) {
		return 4 + (has_concerns ? 1 : 0);
	} else if (n == ROUTE_NAMES.COMPLIANCE_CONTACT) {
		return 5 + (has_concerns ? 1 : 0);
	} else if (n == ROUTE_NAMES.COMPLIANCE_PROCESS) {
		return 6 + (has_concerns ? 1 : 0);
	} else if (n == ROUTE_NAMES.COMPLIANCE_SUBMIT) {
		return 7 + (has_concerns ? 1 : 0);
	} else {
		return 0;
	}
});
const stepTotal = computed((): number => {
	if (!report.value) {
		return 0;
	} else if (report.value.concerns.length > 0) {
		return 8;
	}
	return 7;
});
async function createReport(client_id: string, district_uri: string) {
	let content = {
		client_id: client_id,
		district: district_uri,
		location: {
			accuracy: 0,
			latitude: 0,
			longitude: 0,
		},
	};
	const resp = await fetch("/api/rmo/compliance", {
		body: JSON.stringify(content),
		headers: {
			"Content-Type": "application/json",
		},
		method: "POST",
	});
	if (!resp.ok) {
		const content = await resp.text();
		console.error("Failed to create compliance report", resp.status, content);
		return;
	}
	const body = await resp.json();
	storeLocal.setExistingComplianceReportURI(body.uri);
	report.value!.public_id = body.public_id;
	report.value!.uri = body.uri;
}
function doAddress() {
	if (!report.value) {
		console.log("can't do address, null report");
		return;
	}
	console.log("address done", report.value.address);
	isUploading.value = true;
	updateReport({
		address: report.value.address,
	})
		.then(() => {
			if (!report.value) return;
			if (report.value.concerns.length > 0) {
				router.push(routes.ComplianceConcern(report.value.public_id));
			} else {
				router.push(routes.ComplianceEvidence(report.value.public_id));
			}
		})
		.finally(() => {
			isUploading.value = false;
		});
}
function doEvidence(images: Image[]) {
	if (!report.value) {
		console.log("can't do evidence, null report");
		return;
	}
	(async () => {
		isUploading.value = true;
		let todo = [uploadImages(images)];
		if (report.value && report.value.comments) {
			todo.push(
				updateReport({
					comments: report.value.comments,
				}),
			);
		}
		await Promise.all(todo);
		isUploading.value = false;
	})();
}
function doContact() {
	if (!report.value) {
		console.log("can't do contact, null report");
		return;
	}
	console.log(
		"contact",
		JSON.stringify(report.value.reporter),
		report.value.reporter,
	);
	isUploading.value = true;
	updateReport({
		reporter: report.value.reporter,
	}).finally(() => {
		isUploading.value = false;
	});
}
function doPermission() {
	if (!report.value) {
		console.log("can't do permission, null report");
		return;
	}
	console.log("report.value.has_dog", report.value.has_dog);
	isUploading.value = true;
	updateReport({
		access_instructions: report.value.access_instructions,
		availability_notes: report.value.availability_notes,
		gate_code: report.value.gate_code,
		has_dog: report.value.has_dog,
		permission_type: report.value.permission_type,
		wants_scheduled: report.value.wants_scheduled,
	}).finally(() => {
		isUploading.value = false;
	});
}
function doSubmit() {
	console.log("submit", report.value);
	errorMessage.value = "";
	if (!(report.value && report.value.uri)) {
		return;
	}
	storePublicReport
		.update(report.value.uri, {
			submitted: new Date().toISOString(),
		})
		.then(() => {
			storeLocal.delExistingComplianceReportURI();
			router.push(routes.ComplianceComplete(props.public_id));
		})
		.catch((e: any) => {
			errorMessage.value = e instanceof Error ? e.message : "submit failed";
		});
}
async function updateReport(updates: ComplianceUpdate) {
	if (!(report.value && report.value.uri)) {
		console.log("Refusing to update report without URI");
		return;
	}
	try {
		storePublicReport.update(report.value.uri, updates);
	} catch (e) {
		errorMessage.value =
			e instanceof Error ? e.message : "Failed to update report";
		throw e;
	}
}
async function updateLocation() {
	if (!report.value) return;
	const loc = await storeLocation.get();
	report.value.location = loc.coords;
	updateReport({
		location: report.value.location,
	});
}
async function uploadImages(images: Image[]) {
	if (images.length == 0) return;
	if (!report.value) return;

	isUploading.value = true;
	const formData = new FormData();
	images.map(async (image, index) => {
		formData.append(`image[${index}]`, image.file, image.name);
	});
	const url = `${report.value.uri}/image`;
	const response = await fetch(url, {
		body: formData,
		method: "POST",
	});
	if (!response.ok) {
		const content = await response.text();
		console.error(
			"Failed to POST images",
			url,
			response.status,
			response.statusText,
			content,
		);
		isUploading.value = false;
		return;
	}
	isUploading.value = false;
	// after everything is done update the report so that we see the correct number of images
	// on the report summary
	const r = await storePublicReport.fetchByURI(report.value.uri);
	Object.assign(report.value, r);
}
onMounted(async () => {
	const r = await storePublicReport.fetchByID(props.public_id);
	report.value = r as PublicReportCompliance;
	const d = await storeDistrict.byURI(r.district);
	district.value = d;
	isLoading.value = false;
	const pr = r as PublicReportCompliance;
	if (pr.submitted) {
		router.replace(routes.ComplianceComplete(r.public_id));
	}
});
</script>
